/**
 * InsertBar.tsx
 * Inline block insertion bar for the page builder.
 */
import { useState } from "react";
import type { BlockType } from "@/utils/customPageStore";
import { BLOCK_TYPES } from "./pageBuilderUtils";

interface InsertBarProps {
  onInsert: (type: BlockType) => void;
  isDark: boolean;
}

export default function InsertBar({ onInsert, isDark }: InsertBarProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative flex items-center gap-2 py-1 group/insert">
      <div className={`flex-1 h-px ${isDark ? "bg-gray-700" : "bg-gray-200"} group-hover/insert:bg-pink-300 transition-colors`}></div>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-all opacity-0 group-hover/insert:opacity-100 ${
          isDark
            ? "bg-gray-700 text-gray-400 hover:bg-pink-500 hover:text-white"
            : "bg-gray-100 text-gray-500 hover:bg-pink-500 hover:text-white"
        }`}
        title="Insert block here"
      >
        <i className="ri-add-line text-xs"></i>
        Insert here
      </button>
      <div className={`flex-1 h-px ${isDark ? "bg-gray-700" : "bg-gray-200"} group-hover/insert:bg-pink-300 transition-colors`}></div>

      {open && (
        <div
          className={`absolute top-full left-1/2 -translate-x-1/2 z-20 mt-1 rounded-xl border shadow-lg p-2 grid grid-cols-3 gap-1.5 w-64 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {BLOCK_TYPES.map((bt) => (
            <button
              key={bt.type}
              onClick={() => { onInsert(bt.type); setOpen(false); }}
              className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg cursor-pointer transition-all text-center ${
                isDark ? "hover:bg-gray-700 text-gray-300" : "hover:bg-pink-50 text-gray-600"
              }`}
            >
              <i className={`${bt.icon} text-pink-400 text-base`}></i>
              <span className={`text-[10px] font-bold ${isDark ? "text-gray-300" : "text-gray-700"}`}>{bt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}